import {LazyQueryResult, useLazyQuery} from '@apollo/client';
import {
  QuerySiteDetailsByDomain,
  type SiteDetailsByDomainQuery as SiteForPageListing,
  type SiteDetailsByDomainQueryVariables as SiteForPageListingVariables,
} from '@backstage/attendee-ui-types';
import {
  type PageData,
  type StateSetter,
  buildUpModules,
} from '@backstage/ui-render';
import {
  useManagedRef as useRef,
  useSiteVersionId,
} from '@backstage-components/base';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {config} from '../../../config';
import {useShowId} from '../useShowId';

type Site = SiteForPageListing['site'];

export type SetSiteData = StateSetter<SiteForPageListing | undefined | null>;

interface PageListingResult
  extends Pick<
    LazyQueryResult<SiteForPageListing, SiteForPageListingVariables>,
    'called' | 'error'
  > {
  data: SiteResult | undefined | null;
  fetchSite: () => void;
  setData: SetSiteData;
}

export interface SiteResult extends Omit<SiteForPageListing, 'site'> {
  site: Omit<Site, 'items'> & {items: PageData[]};
  showId?: string;
  domainName?: string;
}

export const usePageListings = (
  options: UsePageListingOptions
): PageListingResult => {
  const {domainName} = options;
  // store `setIsLoading` in a Ref so the refetch hook doesn't need to depend on
  // `setIsLoading`
  const setIsLoadingRef = useRef(options.setIsLoading);
  // `data` is set from the `Promise` returned by calling query via apollo
  const [data, setData] = useState<SiteForPageListing | undefined | null>(
    undefined
  );
  const showId = useShowId(data?.site?.shows ?? [], options.pathShowId);
  const siteVersionId = useSiteVersionId() ?? null;

  // fetch page data, extract showId and add to context for the Attendee Container
  const [getSitePageListings, {called, error}] = useLazyQuery<
    SiteForPageListing,
    SiteForPageListingVariables
  >(QuerySiteDetailsByDomain, {
    fetchPolicy: 'no-cache',
    context: {showId},
  });

  // This is its own call so it can be used as a reloader or the initial load
  // function
  const fetchSite = useCallback(async () => {
    if (typeof domainName === 'undefined') {
      return;
    }
    const setIsLoading = setIsLoadingRef.current;
    setIsLoading(true);
    try {
      const result = await getSitePageListings({
        variables: {domainName, versionId: siteVersionId},
        context: {showId},
      });
      setData((current) =>
        JSON.stringify(current) === JSON.stringify(result.data)
          ? current
          : result.data
      );
      setIsLoading(false);
    } catch (reason) {
      console.error(reason);
    }
  }, [domainName, getSitePageListings, showId, siteVersionId]);
  // Update "loading" state when `called` state changes or `data` changes
  useEffect(() => {
    const setIsLoading = setIsLoadingRef.current;
    const isLoading = !called || typeof data === 'undefined';
    setIsLoading(isLoading);
  }, [called, data]);
  // Set up listener for 'AccessCode:success' event and perform initial fetch
  useEffect(() => {
    // Refetch pages on access token change
    const onAccessCodeSuccess = (): void => {
      const setIsLoading = setIsLoadingRef.current;
      if (typeof showId === 'undefined' || typeof domainName === 'undefined') {
        // This should never happen, `AccessCode:success` can only happen in
        // response to an access code being verified which requires a `showId`
        // and a `domainName` to be set
        console.warn('showId not set but access code verified, WAT');
        return;
      }
      setIsLoading(true);
      fetchSite();
    };
    document.body.addEventListener('AccessCode:success', onAccessCodeSuccess);
    document.body.addEventListener(
      'PublicAccessCode:success',
      onAccessCodeSuccess
    );
    return () => {
      document.body.removeEventListener(
        'AccessCode:success',
        onAccessCodeSuccess
      );
      document.body.removeEventListener(
        'PublicAccessCode:success',
        onAccessCodeSuccess
      );
    };
  }, [domainName, getSitePageListings, fetchSite, showId, siteVersionId]);

  /*
   * Fetch initial data when `domainName` or `showId` changes. `showId` changing
   * will impact the authorization data sent with the request. These dependencies
   * are all in the useCallback, so they trigger this effect.
   */
  useEffect(() => {
    fetchSite();
  }, [fetchSite]);

  // Only rebuild site & page data when `data`, `domainName` or `showId` changes
  const resultData = useMemo(() => {
    // no data yet or error fetching data
    if (typeof data === 'undefined' || data === null) {
      return data;
    }

    const pageData = data.site.pages.map(
      (page): PageData => buildUpModules({config, page, showId, domainName})
    );

    const result: SiteResult = {
      site: {
        ...data.site,
        items: pageData,
      },
      showId,
      domainName,
    };
    return result;
  }, [data, showId, domainName]);

  return {data: resultData, called, error, fetchSite, setData};
};

interface UsePageListingOptions {
  /** DomainName of Site data to be retrieved */
  domainName?: string;
  /** Function called to indicate when the application is in a loading state */
  setIsLoading: StateSetter<boolean>;
  /** Show id candidate parsed out from the pathname of the `URL` */
  pathShowId?: string;
}
